import { Fragment, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { ChatAvatar, initialOf } from "./chat-avatar";
import { onVoiceStarted } from "./voice-playback";
import { ReadingBubble } from "./reading-bubble";
import { ReadingHero } from "./reading-hero";
import {
  READING_STATUS,
  dayLabel,
  expertAvatar,
  mainAnswer,
} from "@lunary/app-core/reading-chat-model";
import type {
  ChatMessage,
  ChatPerson,
  ChatReading,
  ChatViewer,
} from "@lunary/app-core/reading-chat-model";
import styles from "./reading-chat.module.css";

export type ReadingChatProps = {
  reading: ChatReading;
  messages: readonly ChatMessage[];
  viewer: ChatViewer;
  topicLabel: string;
  title: string;
  subtitle?: string;
  onBack?: () => void;
  onHeard?: (messageId: string) => void;
  composer?: ReactNode;
  pendingIds?: readonly string[];
  intro?: ReactNode;
  person?: ChatPerson;
  onReact?: (messageId: string, reaction: number | null) => void;
  queueBusy?: boolean;
};

const BACK = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M15 5l-7 7 7 7" />
  </svg>
);

function StatusLine({
  status,
  viewer,
  busy,
}: {
  status: string;
  viewer: ChatViewer;
  busy: boolean;
}) {
  if (status === "pending" && busy && viewer === "user") {
    return (
      <div className={styles.busyNote} role="status">
        <span className={styles.busyIcon} aria-hidden="true">
          ✦
        </span>
        <p>
          У Людмилы сейчас много разборов, и она записывает их один за другим.
          Она старается и просит ещё немного терпения: хороший разбор стоит
          ожидания.
        </p>
      </div>
    );
  }
  if (status === "pending") {
    return (
      <div className={styles.sys}>
        {viewer === "user"
          ? "Запрос у Людмилы. Она ответит голосом, обычно в течение дня. Пока можно дополнить запрос."
          : "Запрос ждёт разбора."}
      </div>
    );
  }
  if (status === "in_progress") {
    return (
      <div className={styles.sys}>
        <span className={`${styles.st} ${styles.st_work}`}>
          <span className={styles.recdot} />
          {viewer === "user"
            ? "Людмила записывает разбор"
            : "Вы записываете разбор"}
        </span>
      </div>
    );
  }
  if (status === "rejected") {
    return (
      <div className={styles.sys}>
        {viewer === "user"
          ? "Людмила не взяла этот вопрос. Попробуйте сформулировать иначе."
          : "Запрос отклонён."}
      </div>
    );
  }
  if (status === "closed") {
    return <div className={styles.sys}>Разбор завершён</div>;
  }
  if (status === "clarifying") {
    return (
      <div className={styles.sys}>
        {viewer === "user"
          ? "Сообщение у Людмилы. Она ответит здесь же."
          : "Человек написал после разбора."}
      </div>
    );
  }
  return null;
}

export function ReadingChat({
  reading,
  messages,
  viewer,
  topicLabel,
  title,
  subtitle,
  onBack,
  onHeard,
  composer,
  pendingIds = [],
  intro,
  person,
  onReact,
  queueBusy = false,
}: ReadingChatProps) {
  const hero = mainAnswer(messages);
  const end = useRef<HTMLDivElement>(null);
  const count = messages.length;
  const status = READING_STATUS[reading.status];

  useEffect(() => {
    end.current?.scrollIntoView({ block: "end" });
  }, [count, reading.status]);

  useEffect(() => {
    if (!onHeard) return undefined;
    const unheard = new Set(
      messages
        .filter(
          (m) =>
            m.author_role === "expert" &&
            m.kind === "voice" &&
            m.heard_at === null,
        )
        .map((m) => m.id),
    );
    return onVoiceStarted((id) => {
      if (unheard.has(id)) onHeard(id);
    });
  }, [messages, onHeard]);

  const days = messages.map((m) => dayLabel(m.created_at));
  return (
    <div className={styles.chat}>
      <header className={styles.head}>
        {onBack && (
          <button
            type="button"
            className={styles.back}
            onClick={onBack}
            aria-label="Назад"
          >
            {BACK}
          </button>
        )}
        {person ? (
          <ChatAvatar
            src={person.avatarUrl}
            letter={initialOf(person.name, "#")}
            muted
            large
          />
        ) : (
          <ChatAvatar src={expertAvatar(messages)} letter="Л" large />
        )}
        <div className={styles.who}>
          <b>{title}</b>
          <small
            className={
              reading.status === "in_progress" ? styles.live : undefined
            }
          >
            {subtitle ?? topicLabel}
          </small>
        </div>
        {status && (
          <span className={`${styles.st} ${styles[`st_${status.tone}`]}`}>
            {status.label}
          </span>
        )}
      </header>
      <div className={styles.msgs} aria-live="polite">
        {topicLabel && (
          <div className={styles.topics}>
            <span className={styles.topic}>{topicLabel}</span>
          </div>
        )}
        {intro}
        {messages.map((m, i) => {
          const day = days[i] ?? "";
          const showDay = i === 0 || day !== days[i - 1];
          return (
            <Fragment key={m.id}>
              {showDay && <div className={styles.day}>{day}</div>}
              {m === hero ? (
                <ReadingHero
                  hero={m}
                  viewer={viewer}
                  topicLabel={topicLabel}
                  onReact={onReact}
                />
              ) : (
                <ReadingBubble
                  message={m}
                  viewer={viewer}
                  pending={pendingIds.includes(m.id)}
                  person={person}
                  onReact={onReact}
                />
              )}
            </Fragment>
          );
        })}
        <StatusLine
          status={reading.status}
          viewer={viewer}
          busy={queueBusy}
        />
      </div>
      {composer}
      <div ref={end} />
    </div>
  );
}

export function ReadingIntro({
  children,
  avatarUrl,
}: {
  children: ReactNode;
  avatarUrl?: string | null;
}) {
  return (
    <div className={styles.row}>
      <ChatAvatar src={avatarUrl} letter="Л" />
      <div className={styles.bub}>{children}</div>
    </div>
  );
}
